import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

/**
 * Tiered List Sortable Controller
 *
 * Enables drag-and-drop reordering for tiers within lists and items within tiers.
 */
export class TieredListSortableController extends Controller {
  static targets = ["tiersContainer", "itemsContainer"];
  static values = {
    reorderUrl: String, // Base URL pattern, :id will be replaced
  };

  connect() {
    this.sortables = [];
    this.initializeSortables();
  }

  disconnect() {
    this.sortables.forEach((sortable) => sortable.destroy());
    this.sortables = [];
  }

  initializeSortables() {
    // Tiers are sortable within their list
    this.tiersContainerTargets.forEach((container) => {
      this.initializeTiersSortable(container);
    });

    // Items are sortable within and across tiers
    this.itemsContainerTargets.forEach((container) => {
      this.initializeItemsSortable(container);
    });
  }

  initializeTiersSortable(container) {
    const sortable = Sortable.create(container, {
      group: "tiers",
      animation: 150,
      handle: "[data-drag-handle='tier']",
      draggable: "[data-node-type='tier']",
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-drag",
      onEnd: (evt) => this.handleReorder(evt, "tier"),
    });
    this.sortables.push(sortable);
  }

  initializeItemsSortable(container) {
    const sortable = Sortable.create(container, {
      group: "items", // Allow items to move between tiers
      animation: 150,
      handle: "[data-drag-handle='item']",
      draggable: "[data-node-type='item']",
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-drag",
      onEnd: (evt) => this.handleReorder(evt, "item"),
    });
    this.sortables.push(sortable);
  }

  async handleReorder(evt, nodeType) {
    const item = evt.item;
    const nodeId = item.dataset.nodeId;
    const newIndex = evt.newIndex;
    const newParentId = evt.to.dataset.parentId;
    const oldParentId = evt.from.dataset.parentId;

    // Position is 1-indexed for acts_as_list
    const position = newIndex + 1;

    // Check if parent changed (cross-parent move)
    const parentChanged = newParentId !== oldParentId;

    try {
      await this.sendReorderRequest(nodeId, position, parentChanged ? newParentId : null);
      this.showSuccess(nodeType);
    } catch (error) {
      console.error("Reorder failed:", error);
      this.showError(nodeType);
      // Revert the DOM change by refreshing the page
      window.location.reload();
    }
  }

  async sendReorderRequest(nodeId, position, newParentId = null) {
    const url = this.reorderUrlValue.replace(":id", nodeId);

    const body = { position };
    if (newParentId) {
      body.parent_id = newParentId;
    }

    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.content : "";
  }

  showSuccess(nodeType) {
    // Dispatch custom event for toast notification
    this.dispatch("reordered", {
      detail: { nodeType, success: true },
    });
  }

  showError(nodeType) {
    this.dispatch("reorderFailed", {
      detail: { nodeType, success: false },
    });
  }
}
